import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '~/components/common';
import FilterIcon from '~/../symbols/filter.svg';

/* Components */
import FilterCollapse from '../filters/FilterCollapse';
import SimpleMultiSelect from '../filters/SimpleMultiSelect';
import DistanceFilter from '../filters/DistanceFilter';
import { Button, Modal } from '~/components/common';

/* Utilities */
import { trans } from '~/utilities';
import {AnimatePresence, motion} from "framer-motion";

/* Constants */
import { fadeInOut } from '~/constants/animations'

/* Helpers */
import useScrollGradient from '../helpers/useScrollGradient';

export default function VacancyOverviewFilters({
    filters,
    onFilterChange,
    resetFilters,
    totalCount,
    modalOpen,
    setModalOpen,
    isLoading,
    disciplines = [],
    brands = [],
    locations = [],
    workingHours = [],
    experiences = []
}) {

    const [openGroup, setOpenGroup] = useState(null);
    const filterRef = useRef(null);
    const desktopFilterRef = useRef(null);
    useScrollGradient(desktopFilterRef);

    const toggleGroup = (key) => {
        setOpenGroup(prev => (prev === key ? null : key));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openGroup && filterRef.current && !filterRef.current.contains(event.target)) {
                setOpenGroup(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openGroup]);

    return (
        <>
            <Button
                title={trans('app.vacancy_overview.filter_button')}
                theme="primary"
                className="btn-block justify-content-between mb-4 d-lg-none"
                onClick={() => setModalOpen(true)}
            />
            <Modal
                open={modalOpen}
                handleClose={() => setModalOpen(false)}
                backText={trans('app.content_block.vehicles.filter.options.modal_back')}
                modalTitle='Filteren'
                modalBody={
                    <>
                        <div className="mb-20 mx-n2">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="font-weight-bold text--14">{trans('app.vacancy_overview.filtered_title')}</span>
                                <Button
                                    title={trans('app.vacancy_overview.reset_filters')}
                                    theme="secondary"
                                    className="btn-sm btn-inline-block text-base flex-row-reverse"
                                    onClick={() => resetFilters()}
                                />
                            </div>

                            <div className="filters filters--hidden" ref={filterRef}>
                                <AnimatePresence>
                                    {openGroup && (
                                        <motion.div key="overlay" className="overlay d-lg-none" {...fadeInOut} onClick={() => { setOpenGroup(null); }}></motion.div>
                                    )}
                                </AnimatePresence>
                                <div className="filters__inner">
                                    <ul className="filter-list">
                                        <div className="filter-list-start"></div>
                                        <li className="filter-list__item d-none d-lg-block">
                                            <div className="align-items-center gap-2 font-weight-bold text--16 mr-4 d-flex py-2">
                                                <Icon
                                                    size={24}
                                                    url={FilterIcon.url}
                                                />
                                                Filteren
                                            </div>
                                        </li>

                                        {/* Disciplines */}
                                        {disciplines.length > 1 &&
                                            <li className="filter-list__item w-100">
                                                <FilterCollapse
                                                    filterKey="disciplines"
                                                    title={trans('app.vacancy_overview.filters.disciplines.label')}
                                                    isOpen={openGroup === 'disciplines'}
                                                    onToggle={() => toggleGroup('disciplines')}
                                                    activeCount={filters.disciplines.length}
                                                    resetFilter={() => onFilterChange('disciplines', [])}
                                                >
                                                    <SimpleMultiSelect
                                                        options={disciplines.map(d => ({ value: d, label: d }))}
                                                        selectedValues={filters.disciplines}
                                                        onChange={(newValues) => onFilterChange('disciplines', newValues)}
                                                    />
                                                </FilterCollapse>
                                            </li>
                                        }

                                        {/* Brands */}
                                        {brands.length > 1 &&
                                            <li className="filter-list__item w-100">
                                                <FilterCollapse
                                                    filterKey="brands"
                                                    title={trans('app.vacancy_overview.filters.brand.label')}
                                                    isOpen={openGroup === 'brands'}
                                                    onToggle={() => toggleGroup('brands')}
                                                    activeCount={filters.brands.length}
                                                    resetFilter={() => onFilterChange('brands', [])}
                                                >
                                                    <SimpleMultiSelect
                                                        options={brands.map(b => ({ value: b, label: b }))}
                                                        selectedValues={filters.brands}
                                                        onChange={(newValues) => onFilterChange('brands', newValues)}
                                                    />
                                                </FilterCollapse>
                                            </li>
                                        }

                                        {/* Locations */}
                                        {locations.length > 1 &&
                                            <li className="filter-list__item w-100">
                                                <FilterCollapse
                                                    filterKey="locations"
                                                    title={trans('app.vacancy_overview.filters.location.label')}
                                                    isOpen={openGroup === 'locations'}
                                                    onToggle={() => toggleGroup('locations')}
                                                    activeCount={filters.locations.length}
                                                    resetFilter={() => onFilterChange('locations', [])}
                                                >
                                                    <SimpleMultiSelect
                                                        options={locations.map(l => ({ value: l, label: l }))}
                                                        selectedValues={filters.locations}
                                                        onChange={(newValues) => onFilterChange('locations', newValues)}
                                                    />
                                                </FilterCollapse>
                                            </li>
                                        }

                                        {/* Working Hours */}
                                        {workingHours.length > 1 &&
                                            <li className="filter-list__item w-100">
                                                <FilterCollapse
                                                    filterKey="working_hours"
                                                    title={trans('app.vacancy_overview.filters.working_hours.label')}
                                                    isOpen={openGroup === 'working_hours'}
                                                    onToggle={() => toggleGroup('working_hours')}
                                                    activeCount={filters.working_hours.length}
                                                    resetFilter={() => onFilterChange('working_hours', [])}
                                                >
                                                    <SimpleMultiSelect
                                                        options={workingHours}
                                                        selectedValues={filters.working_hours}
                                                        onChange={(newValues) => onFilterChange('working_hours', newValues)}
                                                    />
                                                </FilterCollapse>
                                            </li>
                                        }

                                        {/* Experiences */}
                                        {experiences.length > 1 &&
                                            <li className="filter-list__item w-100">
                                                <FilterCollapse
                                                    filterKey="experiences"
                                                    title={trans('app.vacancy_overview.filters.experience.label')}
                                                    isOpen={openGroup === 'experiences'}
                                                    onToggle={() => toggleGroup('experiences')}
                                                    activeCount={filters.experiences.length}
                                                    resetFilter={() => onFilterChange('experiences', [])}
                                                >
                                                    <SimpleMultiSelect
                                                        options={experiences}
                                                        selectedValues={filters.experiences}
                                                        onChange={(newValues) => onFilterChange('experiences', newValues)}
                                                    />
                                                </FilterCollapse>
                                            </li>
                                        }

                                        {/* Distance */}
                                        <li className="filter-list__item w-100">
                                            <FilterCollapse
                                                filterKey="distance"
                                                title="Afstand"
                                                isOpen={openGroup === 'distance'}
                                                onToggle={() => toggleGroup('distance')}
                                                activeCount={filters.distance?.postcode && filters.distance?.radius ? 1 : 0}
                                                resetFilter={() => onFilterChange('distance', { postcode: '', radius: '' })}
                                            >
                                                <DistanceFilter
                                                    value={filters.distance}
                                                    onChange={(newVal) => onFilterChange('distance', newVal)}
                                                />
                                            </FilterCollapse>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="filter-button-fixed">
                            <Button
                                title={trans('app.vacancy_overview.result_button', { 'result': totalCount})}
                                theme="primary"
                                className={`btn-block justify-content-between ${ isLoading ? 'btn--loader' : '' }`.trim()}
                                onClick={() => setModalOpen(false)}
                            />
                        </div>
                    </>
                }
            />


            <div className="filters filters--hidden filters--desktop" ref={filterRef}>
                <AnimatePresence>
                    {openGroup && (
                        <motion.div key="overlay" className="overlay d-lg-none" {...fadeInOut} onClick={() => { setOpenGroup(null); }}></motion.div>
                    )}
                </AnimatePresence>
                <div className="filters__inner nav--scroll-mobile scroll-start" ref={desktopFilterRef}>
                    <div data-scroll-start></div>
                    <ul className="filter-list">
                        <div className="filter-list-start"></div>
                        <li className="filter-list__item d-none d-lg-block">
                            <div className="align-items-center gap-2 font-weight-bold text--16 mr-4 d-flex py-2">
                                <Icon
                                    size={24}
                                    url={FilterIcon.url}
                                />
                                Filteren
                            </div>
                        </li>

                        {/* Disciplines */}
                        {disciplines.length > 1 &&
                            <li className="filter-list__item">
                                <FilterCollapse
                                    filterKey="disciplines"
                                    title={trans('app.vacancy_overview.filters.disciplines.label')}
                                    isOpen={openGroup === 'disciplines'}
                                    onToggle={() => toggleGroup('disciplines')}
                                    activeCount={filters.disciplines.length}
                                    resetFilter={() => onFilterChange('disciplines', [])}
                                >
                                    <SimpleMultiSelect
                                        options={disciplines.map(d => ({ value: d, label: d }))}
                                        selectedValues={filters.disciplines}
                                        onChange={(newValues) => onFilterChange('disciplines', newValues)}
                                    />
                                </FilterCollapse>
                            </li>
                        }

                        {/* Brands */}
                        {brands.length > 1 &&
                            <li className="filter-list__item">
                                <FilterCollapse
                                    filterKey="brands"
                                    title={trans('app.vacancy_overview.filters.brand.label')}
                                    isOpen={openGroup === 'brands'}
                                    onToggle={() => toggleGroup('brands')}
                                    activeCount={filters.brands.length}
                                    resetFilter={() => onFilterChange('brands', [])}
                                >
                                    <SimpleMultiSelect
                                        options={brands.map(b => ({ value: b, label: b }))}
                                        selectedValues={filters.brands}
                                        onChange={(newValues) => onFilterChange('brands', newValues)}
                                    />
                                </FilterCollapse>
                            </li>
                        }

                        {/* Locations */}
                        {locations.length > 1 &&
                            <li className="filter-list__item">
                                <FilterCollapse
                                    filterKey="locations"
                                    title={trans('app.vacancy_overview.filters.location.label')}
                                    isOpen={openGroup === 'locations'}
                                    onToggle={() => toggleGroup('locations')}
                                    activeCount={filters.locations.length}
                                    resetFilter={() => onFilterChange('locations', [])}
                                >
                                    <SimpleMultiSelect
                                        options={locations.map(l => ({ value: l, label: l }))}
                                        selectedValues={filters.locations}
                                        onChange={(newValues) => onFilterChange('locations', newValues)}
                                    />
                                </FilterCollapse>
                            </li>
                        }

                        {/* Working Hours */}
                        {workingHours.length > 1 &&
                            <li className="filter-list__item">
                                <FilterCollapse
                                    filterKey="working_hours"
                                    title={trans('app.vacancy_overview.filters.working_hours.label')}
                                    isOpen={openGroup === 'working_hours'}
                                    onToggle={() => toggleGroup('working_hours')}
                                    activeCount={filters.working_hours.length}
                                    resetFilter={() => onFilterChange('working_hours', [])}
                                >
                                    <SimpleMultiSelect
                                        options={workingHours}
                                        selectedValues={filters.working_hours}
                                        onChange={(newValues) => onFilterChange('working_hours', newValues)}
                                    />
                                </FilterCollapse>
                            </li>
                        }

                        {/* Experiences */}
                        {experiences.length > 1 &&
                            <li className="filter-list__item">
                                <FilterCollapse
                                    filterKey="experiences"
                                    title={trans('app.vacancy_overview.filters.experience.label')}
                                    isOpen={openGroup === 'experiences'}
                                    onToggle={() => toggleGroup('experiences')}
                                    activeCount={filters.experiences.length}
                                    resetFilter={() => onFilterChange('experiences', [])}
                                >
                                    <SimpleMultiSelect
                                        options={experiences}
                                        selectedValues={filters.experiences}
                                        onChange={(newValues) => onFilterChange('experiences', newValues)}
                                    />
                                </FilterCollapse>
                            </li>
                        }

                        {/* Distance */}
                        <li className="filter-list__item">
                            <FilterCollapse
                                filterKey="distance"
                                title="Afstand"
                                isOpen={openGroup === 'distance'}
                                onToggle={() => toggleGroup('distance')}
                                activeCount={filters.distance?.postcode && filters.distance?.radius ? 1 : 0}
                                resetFilter={() => onFilterChange('distance', { postcode: '', radius: '' })}
                            >
                                <DistanceFilter
                                    value={filters.distance}
                                    onChange={(newVal) => onFilterChange('distance', newVal)}
                                />
                            </FilterCollapse>
                        </li>
                    </ul>
                    <div data-scroll-end></div>
                </div>
            </div>
            <hr className="my-4" />
            <div className="d-flex align-items-center gap-4 mb-6">
                <div className="no-wrap text--18">{trans('app.vacancy_overview.result', { 'result': totalCount})}</div>

                <Button
                    title={trans('app.vacancy_overview.reset_filters')}
                    theme="secondary"
                    className="d-none d-md-inline-block btn-sm"
                    onClick={() => resetFilters()}
                />
            </div>
        </>
    );
}
